import { Component, useEffect, useState } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { RipplePage } from './app/RipplePage'
import { readUrl } from './app/DemoBar'
import type { Session } from './app/DemoBar'
import { IconAlert, MilliCoin, IconChevronRight } from './icons'
import { asset } from './lib/asset'
import { fetchAccount, hasToken, isDemoPinned, loginUrl } from './lib/account'
import './styles/tokens.css'
import './styles/app.css'

/**
 * 页面外壳。
 *
 * 背景：这个工具的归属改过两次，最后落在**米羊 Lab 底下**。
 *   1. 起初导航写「写完啦」、指向 tools.xiewanla.com——那是另一条产品线的合规壳，
 *      从米羊点进来的人会以为跳错了站（2026-09-10 用户裁决改掉）。
 *   2. 接着按「米羊 AI 工具箱」做，站点根还做了一张工具箱首页，列出主站那五个工具。
 *      同一天用户裁掉了：「别的不要，只留这一个」，随后给出理由——
 *      「你直接放在米羊 Lab 里吧，这样更顺一点。因为这个其实会发 blog 到米羊 Lab 里。
 *       就是米羊 Lab 里有工具入口，这样的。」
 *
 *   这个理由是对的，而且比「工具箱」更站得住：数字涟漪是一篇研究
 *   （Asterove_yeoui 的 `docs/paper/2026-08-24-digital-ripple-analysis.md`），
 *   这个工具是那篇研究的可执行版本。读者读完文章想验一张自己的图，入口就该在文章旁边。
 *   放进「工具箱」则是把它与抠图、翻译并列成一个通用小工具，那是另一种叙事，
 *   而它既没有工具箱的量（只有一个），也丢掉了研究这个来路。
 *
 * 设计意图：面包屑写「米羊 / Lab / 数字涟漪修复」。Lab 那一段指 `lab.miyang.cn/`——
 *   2026-09-10 用户裁决把整个 Lab 搬到这个子域，这个工具是它下面的 `/ripple`，
 *   于是「Lab 里有工具入口」在地址里就是字面成立的，而不是靠一句面包屑说服人。反向的入口在 Lab 页面上，
 *   见 miyang.cn 的 `templates/research.html` 里那一段「研究里长出来的工具」。
 *   视觉沿用 miyang.cn 的令牌与 64px 导航高，两页之间切换不断裂。
 * 关键约束：
 *   1. 账号体系是米羊的，本站不自建登录。登录、充值、控制台全部指回 miyang.cn。
 *   2. 余额只在登录态显示。未登录时显示余额位等于告诉用户「你有个户头」，
 *      而他此时并没有。
 *   3. session 提到这一层，是因为导航要按它换内容（余额 / 登录），
 *      而它同时又是页面里那道登录闸的输入。两处必须是同一个值，不能各存一份。
 *      它的初值来自米羊账号（见 lib/account.ts）：用户在 miyang.cn 登录过，
 *      cookie 就在 `.miyang.cn` 上，这一页开页时问一次网关换成真实身份与余额。
 *      演示条仍可在其之上手动改——那是脚手架的用途，不是登录的旁路。
 *   4. 只有一条路径。这个包只装这一个工具，挂在 `/ripple`（见 vite.config.ts 的 base），
 *      内部不做路由。
 */
/**
 * 渲染出错时的兜底。
 *
 * React 没有边界时，任何一处渲染异常都会把整棵树卸载：页面变成纯白，控制台里那行
 * 报错用户看不见，他看到的只是「一直没动静」——而作业其实早在服务端跑完了
 * （2026-09-10 实跑撞到：完成屏硬取 snap.after，遇上「没有成品」那一支就崩）。
 * 那个具体的洞已经补了，这道边界防的是下一个：宁可显示一句「这一屏没画出来」，
 * 也不能让用户对着白页等。
 *
 * 只包住工作区，不包导航与页脚——外壳还在，用户至少知道自己还在这个站上。
 */
class Boundary extends Component<{ children: ReactNode }, { err: Error | null }> {
  state = { err: null as Error | null }

  static getDerivedStateFromError(err: Error) {
    return { err }
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    console.error('[ripple] 这一屏没画出来：', err, info.componentStack)
  }

  render() {
    if (!this.state.err) return this.props.children
    return (
      <div className="verdict bad" style={{ margin: '28px 0' }}>
        <div className="verdict-top">
          <IconAlert size={20} />
          <h3>这一屏没画出来</h3>
        </div>
        <p>
          {'页面出了个自己的问题，跟你的图无关：处理如果已经开始，服务端那边照常在跑，'}
          {'米粒也按实际结果算。'}
        </p>
        <p style={{ marginTop: 8 }}>
          <b>刷新一次通常就好；还是这样的话，把这一页的时间点发给我们。</b>
        </p>
        <div className="gate-acts" style={{ marginTop: 16 }}>
          <button className="btn btn-pri" onClick={() => location.reload()}>刷新页面</button>
        </div>
      </div>
    )
  }
}

export default function App() {
  const [session, setSession] = useState<Session>(() => readUrl().session)
  const balance = session.kind === 'user' ? session.balance : null

  /* 带着令牌进来的人，先把导航右侧那一格留白，等网关回话再画。
     先画「登录」再换成余额，对一个已经登录的人来说是「我掉线了？」。 */
  const [probing, setProbing] = useState(() => !isDemoPinned() && hasToken())

  useEffect(() => {
    if (!probing) return
    const abort = new AbortController()
    fetchAccount(abort.signal)
      .then(account => {
        if (abort.signal.aborted) return
        if (account) setSession({ kind: 'user', balance: account.balance })
        setProbing(false)
      })
    // 只在开页时问一次：令牌过期与余额变动都由处理请求的响应来纠正。
    return () => abort.abort()
  }, [])

  return (
    <>
      <nav className="nav">
        <div className="wrap nav-in">
          <div className="brand">
            <a href="https://miyang.cn" className="logo">
              <img src={asset('/img/miyang-logo.png')} alt="米羊" width="30" height="30" draggable={false} />
              <b>米羊</b>
            </a>
            <a href="https://lab.miyang.cn/" className="crumb">Lab</a>
            <IconChevronRight size={14} />
            {/* 末节也是链接，指回工具首页（2026-09-12 用户裁决：「点击这里，要到涟漪首页」）。
                走 href 而不是页内重置：地址栏上的 `?job=` / `?state=` 要一起清掉，
                而页内那条「换一张图」会取消手里那趟作业——米粒还预扣着，
                从面包屑回首页不该有这个副作用。导航只是松手，作业照跑，
                回头在「我处理过的」里找得到。 */}
            <a href={asset('/')} className="crumb on">数字涟漪修复</a>
          </div>
          {/* 右上角这三条与 Lab 的导航逐条一致（miyang.cn 的
              templates/partials/research_nav.html）：米羊首页、关于我们、加入我们。
              2026-09-10 用户裁决：「工具首页参考 Lab 的页面设置，右上角就是这些东西。」
              这一页挂在 lab.miyang.cn 下，导航跟 Lab 一样，才不像另开的一个站。
              回 Lab 首页那条不在这里——左上角的面包屑已经是它。 */}
          <div className="nav-links">
            <a href="https://miyang.cn/">米羊首页</a>
            <a href="https://miyang.cn/#about">关于我们</a>
            <a href="https://miyang.cn/careers">加入我们</a>
          </div>
          <div className="nav-r">
            {probing ? null : balance != null ? (
              <>
                <span className="bal">
                  <MilliCoin size={15} />
                  <span>余额 <b>{balance.toLocaleString('en-US')}</b> 米粒</span>
                </span>
                <a href="https://miyang.cn/console" className="btn btn-sm">控制台</a>
              </>
            ) : (
              <a href={loginUrl()} className="btn btn-pri btn-sm">
                登录
              </a>
            )}
          </div>
        </div>
      </nav>

      <div className="wrap">
        <Boundary>
          <RipplePage session={session} onSession={setSession} />
        </Boundary>
      </div>

      {/* 页脚的备案信息与主站逐字一致（miyang.cn 的 templates/index.html）。
          2026-09-10 用户裁决：「就直接拿着首页的备案信息直接用上就行了。不要没有备案，
          看起来很山寨。」此前这里只有一行「© 米羊科技（深圳）有限公司」，既没有备案号、
          也没有增值电信许可证，而这一页是要收钱的——一个收费页面没有备案号，
          第一眼就不像一家公司在做的事。邮箱同时改回主站那个（contact@，不是 connect@）。

          ⚠️ 这三样（公司名、备案号、许可证号）是法定信息，改动只能跟着主站改，
          不要在这里另起一套写法。 */}
      <footer className="ft">
        <div className="wrap">
          {/* 这里原本还有一排五条链接（米羊首页 / 米羊 Lab / 控制台 / 隐私政策 / 联系我们）。
              2026-09-10 用户裁决：「最底下的那些链接乱七八糟的是不是可以不要了。」
              前四条现在都在页面顶上——米羊首页在导航右侧，米羊 Lab 是左上角的面包屑，
              控制台是登录后的那颗按钮——底下再列一遍只是把同一件事说两次。
              隐私政策跟着版权行走（Lab 的页脚也是这么放的），法定信息该在场但不抢注意力。 */}
          {/* 「登录后产物一直保留」改成「产物存进你的米羊盘」：留得住是因为进了盘
              （2026-09-12 起每趟都交进去），首尔这台机器上的那一份是临时的。
              页面不该承诺一件只有盘才做得到的事，却指着首尔。 */}
          <div className="ft-meta">
            {'处理按'}<MilliCoin size={13} inline />{'米粒计费 · 产物存进你的米羊盘'}
          </div>
          <div className="ft-legal">
            {'Copyright © 2024–2026 米羊科技（上海）有限公司、米羊科技（深圳）有限公司 · '}
            <a href="https://miyang.cn/privacy">隐私政策</a>
            {' · '}
            <a href="https://miyang.cn/terms">服务条款</a>
          </div>
          <div className="ft-legal">
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">
              粤ICP备2023103765号-2
            </a>
            {' · 增值电信业务经营许可证：粤B2-20231240'}
          </div>
        </div>
      </footer>
    </>
  )
}
